/**
 * NOTAS FISCAIS NO APARELHO (D4) — as notas registradas e a fila delas.
 *
 * Mesmo jeito da catira e do extrato: guarda no localStorage, por pessoa (a
 * chave leva o id de quem entrou), e o que foi registrado aqui fica PENDENTE
 * até o «Sincronizar». Sem sinal funciona: a nota lida pela câmera ou pelo
 * XML fica no aparelho e sobe no botão, DEPOIS do registro a que se liga (a
 * ficha, a despesa, o movimento da catira) — o servidor espera o registro
 * chegar, sem marcar recusa.
 *
 * O arquivo (XML, PDF ou foto) fica no IndexedDB (arquivos.ts) com o mesmo
 * id que vai ao banco e ao R2, e sobe pela fila de arquivos da 2G, logo
 * depois da nota.
 *
 * Nada se apaga: nota errada é ANULADA com motivo, e a anulação também entra
 * na fila. O que o servidor recusa de vez sai da fila e fica aqui marcado
 * «recusada pelo servidor», riscada e fora das listas.
 *
 * Ao mudar o formato dos dados guardados, mudar a versão da CHAVE.
 */
import { useSyncExternalStore } from 'react'
import { lerDono, lerEstado } from './armazem'
import { sincronizarArquivos } from './arquivos'
import { lerCatira } from './catira'
import { lerExtrato } from './extrato'
import { api, Recusa } from './sessao'
import type { Ligacao, Nota } from '../dominio/notas'

// tipos e contas moram em src/dominio/notas.ts (testados em testes/notas.mjs)
export type { ItemDaNota, LeituraDaNota, Ligacao, LinhaDoPeriodo, Nota, OrigemDaNota, Sentido, TipoDeLigacao } from '../dominio/notas'
export {
  acharChave, chaveDoPdf, chaveValida, descricaoDaNota, documentoDaNota, formatarChave, infoDaChave, lerCodigo, lerXmlDaNota,
  NOME_DA_ORIGEM, nomeDoModelo, notaComAChave, notasDoPeriodo, notaValendo, sentidoSugerido, totaisDoPeriodo, XmlRecusado,
} from '../dominio/notas'

export interface EstadoDasNotas {
  notas: Nota[]
  /** ids de notas registradas ou anuladas aqui que ainda não subiram */
  pendentes: string[]
  ultimaSync: string | null
}

const PREFIXO = 'pesagem.notas.v1.'

function vazio(): EstadoDasNotas {
  return { notas: [], pendentes: [], ultimaSync: null }
}

function completar(e: Partial<EstadoDasNotas> | null): EstadoDasNotas {
  if (!e || typeof e !== 'object') return vazio()
  return {
    notas: Array.isArray(e.notas) ? e.notas : [],
    pendentes: Array.isArray(e.pendentes) ? e.pendentes : [],
    ultimaSync: e.ultimaSync ?? null,
  }
}

let donoAberto: string | null = null
let estado = vazio()
const ouvintes = new Set<() => void>()

/** Os dados são de quem entrou: confere a cada leitura e troca quando a pessoa mudou. */
function garantirDono(): void {
  const id = lerDono()?.id ?? null
  if (id === donoAberto) return
  donoAberto = id
  try {
    const t = id ? localStorage.getItem(PREFIXO + id) : null
    estado = completar(t ? JSON.parse(t) : null)
  } catch {
    estado = vazio()
  }
}

export function usarNotas(): EstadoDasNotas {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => { garantirDono(); return estado },
  )
}

export function lerNotas(): EstadoDasNotas {
  garantirDono()
  return estado
}

/** Toda alteração passa por aqui. */
function mudarNotas(fn: (e: EstadoDasNotas) => void): void {
  garantirDono()
  const novo = structuredClone(estado)
  fn(novo)
  estado = novo
  try { if (donoAberto) localStorage.setItem(PREFIXO + donoAberto, JSON.stringify(estado)) } catch { /* sem disco: a tela das fichas já avisa */ }
  ouvintes.forEach((f) => f())
}

/** As notas ligadas a um registro (a ficha, a despesa, o movimento), anuladas inclusive — nada some. */
export function notasLigadas(notas: Nota[], l: Ligacao): Nota[] {
  return notas.filter((n) => n.ligacao?.tipo === l.tipo && n.ligacao.id === l.id)
}

/* ---------- registrar e anular (com ou sem sinal) ---------- */

export function registrarNota(n: Nota): void {
  mudarNotas((e) => {
    e.notas.push(n)
    if (!e.pendentes.includes(n.id)) e.pendentes.push(n.id)
  })
}

export function anularNota(id: string, motivo: string, porId: string): void {
  mudarNotas((e) => {
    const n = e.notas.find((x) => x.id === id)
    if (!n || n.anulacao || n.recusa) return
    n.anulacao = { em: new Date().toISOString(), por: porId, motivo }
    if (!e.pendentes.includes(id)) e.pendentes.push(id)
  })
}

/* ---------- sincronizar (chamado pelo «Sincronizar» de fila.ts) ---------- */

export interface ContagemDasNotas { subiram: number; desceram: number; recusas: string[] }

/**
 * Sobe as pendentes, desce o que mudou e, se subiu nota com arquivo, já manda
 * o arquivo junto (senão ele ficaria para o próximo «Sincronizar»: a fila de
 * arquivos roda antes desta). Sem sinal: lança `SemSinal` (a fila para tudo,
 * nada se perde). Recusa do servidor vira frase na tela e NÃO derruba o resto
 * — inclusive se o banco ainda não tiver a migração 009.
 */
export async function sincronizarNotas(r: ContagemDasNotas): Promise<void> {
  if (!lerDono()) return
  let comArquivo = false
  try {
    comArquivo = await subir(r)
    await descer(r)
  } catch (e) {
    if (e instanceof Recusa) { r.recusas.push(`Notas: ${e.message}`); return }
    throw e
  }
  if (comArquivo) await sincronizarArquivos(r)
}

/** «o registro desta nota ainda não chegou ao banco»: fica na fila, sem alarde */
function esperar(e: Recusa, n: Nota): boolean {
  return e.status === 409 && e.dados?.['erro'] === 'registro_ausente' && !registroRecusado(n)
}

/** Recusa definitiva: 4xx que não seja 401 nem a espera acima. 5xx tenta de novo. */
function deVez(e: Recusa, n: Nota): boolean {
  return !esperar(e, n) && e.status >= 400 && e.status < 500 && e.status !== 401
}

/**
 * R1A · contador preso: o registro a que a nota se liga foi RECUSADO pelo
 * servidor neste aparelho (a ficha, a despesa, o movimento da catira). Ele
 * nunca vai chegar ao banco — esperar por ele seria esperar para sempre.
 */
function registroRecusado(n: Nota): boolean {
  const l = n.ligacao
  if (!l) return false
  if (l.tipo === 'ficha') {
    const e = lerEstado()
    return l.id in e.recusadas.fichas && !e.pendentes.fichas.includes(l.id)
  }
  if (l.tipo === 'despesa') return !!lerExtrato().lancamentos.find((x) => x.id === l.id)?.recusa
  return !!lerCatira().movimentos.find((x) => x.id === l.id)?.recusa
}

/**
 * R1A · «Sair»: fica só o que ainda não subiu (nada se perde); as notas que
 * já estão no banco saem do aparelho e voltam inteiras no próximo «Sincronizar».
 */
export function enxugarNotasAoSair(): void {
  garantirDono()
  if (!donoAberto) return
  mudarNotas((e) => {
    e.notas = e.notas.filter((n) => e.pendentes.includes(n.id))
    e.ultimaSync = null
  })
}

function dataBR(d: string): string {
  return d.split('-').reverse().join('/')
}

/** Devolve se subiu alguma nota com arquivo (o arquivo vai logo em seguida). */
async function subir(r: ContagemDasNotas): Promise<boolean> {
  let comArquivo = false
  for (const id of [...lerNotas().pendentes]) {
    const n = lerNotas().notas.find((x) => x.id === id)
    if (!n) { tirarDaFila(id); continue }
    try {
      await api('notas', {
        id: n.id, chave: n.chave, origem: n.origem, sentido: n.sentido, data: n.data, valor: n.valor,
        numero: n.numero, serie: n.serie, emitente_doc: n.emitenteDoc, emitente_nome: n.emitenteNome,
        destinatario_doc: n.destinatarioDoc, destinatario_nome: n.destinatarioNome, descricao: n.descricao,
        itens: n.itens, url_consulta: n.urlConsulta, arquivo_id: n.arquivoId, ligacao: n.ligacao,
        anulacao: n.anulacao ? { motivo: n.anulacao.motivo } : undefined,
      })
      mudarNotas((e) => {
        // só sai da fila se ninguém anulou enquanto subia
        const x = e.notas.find((y) => y.id === id)
        if (!x || !!x.anulacao === !!n.anulacao) e.pendentes = e.pendentes.filter((y) => y !== id)
      })
      if (n.arquivoId) comArquivo = true
      r.subiram++
    } catch (e) {
      if (!(e instanceof Recusa)) throw e
      if (esperar(e, n)) continue
      const motivo = e.dados?.['erro'] === 'registro_ausente' ? 'o registro dela foi recusado pelo servidor e não vai chegar ao banco.' : e.message
      const frase = `Nota de ${dataBR(n.data)}: ${motivo}`
      if (e.status === 409 && e.dados?.['codigo'] === 'nao_anulou') {
        // a anulação não valeu: a nota volta a valer aqui
        mudarNotas((x) => {
          const y = x.notas.find((z) => z.id === id)
          if (y) y.anulacao = null
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
        r.recusas.push(frase)
        continue
      }
      if (deVez(e, n)) {
        // Mandar de novo daria a mesma resposta para sempre (ex.: a mesma chave
        // já registrada por outro aparelho): o contador nunca zeraria e a
        // versão nova do app nunca seria oferecida. Sai da fila e fica aqui
        // riscada, fora das listas — nada se apaga.
        mudarNotas((x) => {
          const y = x.notas.find((z) => z.id === id)
          if (y) y.recusa = { em: new Date().toISOString(), motivo }
          x.pendentes = x.pendentes.filter((z) => z !== id)
        })
        r.recusas.push(`${frase} — não entrou.`)
        continue
      }
      r.recusas.push(frase)
    }
  }
  return comArquivo
}

async function descer(r: ContagemDasNotas): Promise<void> {
  const desde = lerNotas().ultimaSync
  const q = desde ? `?desde=${encodeURIComponent(desde)}` : ''
  const d = await api<{ agora: string; notas: Nota[] }>(`notas${q}`)
  mudarNotas((e) => {
    for (const nova of d.notas ?? []) {
      if (e.pendentes.includes(nova.id)) continue // a mudança daqui ainda vai subir
      const i = e.notas.findIndex((x) => x.id === nova.id)
      if (i >= 0) {
        const antes = e.notas[i]!
        if (!!antes.anulacao !== !!nova.anulacao || antes.recusa) r.desceram++
        // o nome do arquivo só existe no aparelho que o anexou: não perder
        e.notas[i] = antes.arquivo ? { ...nova, arquivo: antes.arquivo } : nova
      } else {
        e.notas.push(nova)
        r.desceram++
      }
    }
    e.ultimaSync = d.agora || e.ultimaSync
  })
}

function tirarDaFila(id: string): void {
  mudarNotas((e) => { e.pendentes = e.pendentes.filter((y) => y !== id) })
}
